/** Alarm Lab strategies kept on the server as live rules (db/036,
 *  /api/strategies). They replaced workspace v5's browser library: a
 *  strategy belongs to a project, its members read it, its Editors and
 *  Admins change it, and switched on it is judged by the rule monitor every
 *  round, raising the same events, emails and chat posts as any alarm rule. */
import { api } from '../api/client'
import type { StrategyRecord } from '../api/types'
import type { LabStrategy } from './alarmLab'
import type { AnalysisSource } from './analysisSources'
import { calculatedSourceId, type CalculatedSensor } from './calculatedSensors'
import { manualSourceId, type ManualSensor } from './manualSensors'
import { remoteCollection } from './remoteStore'

export const STRATEGIES_EVENT = 'hydro:strategies-changed'

/** Every strategy in the reader's projects, held once for the Lab and the
 *  Alerts page. */
export const strategyStore = remoteCollection<StrategyRecord>(
  () => api.strategies.list().then(data => data.strategies), STRATEGIES_EVENT)

/** The rule a saved strategy is (`rule-12` → 12); null for a draft not yet saved. */
export function strategyRuleId(strategy: Pick<LabStrategy, 'id'>): number | null {
  const match = /^rule-(\d+)$/.exec(strategy.id)
  return match ? Number(match[1]) : null
}

/** Save a strategy in a project: a new one (saved off) when it has no rule
 *  yet, else that rule. A change to what a live strategy watches closes its
 *  open event on the server. */
export async function saveStrategy(strategy: LabStrategy, projectId: number): Promise<StrategyRecord> {
  const id = strategyRuleId(strategy)
  const { strategy: saved } = id === null
    ? await api.strategies.create({ project_id: projectId, strategy })
    : await api.strategies.update(id, { project_id: projectId, strategy })
  await strategyStore.refresh()
  // Should the list not come back, what was saved is still shown.
  const held = strategyStore.snapshot().items
  if (!held.some(r => r.id === saved.id)) strategyStore.set([...held, saved])
  return saved
}

/** Switch a strategy live or off. The server holds the platform-wide cap. */
export async function setStrategyLive(id: number, enabled: boolean): Promise<StrategyRecord> {
  const { strategy } = await api.strategies.update(id, { enabled })
  await strategyStore.refresh()
  return strategy
}

export async function deleteStrategy(id: number): Promise<void> {
  await api.strategies.remove(id)
  await strategyStore.refresh()
}

/** What a strategy in a project may read: that project's stations'
 *  channels, and its own calculated and manual sensors (the server checks
 *  the same, services/strategies.py _check_sources). */
export function projectSources(sources: AnalysisSource[], stations: ReadonlySet<string>, projectId: number,
    calculated: Pick<CalculatedSensor, 'id' | 'project_id'>[], manual: Pick<ManualSensor, 'id' | 'project_id'>[]): AnalysisSource[] {
  const own = new Set([
    ...calculated.filter(c => c.project_id === projectId).map(c => calculatedSourceId(c.id)),
    ...manual.filter(m => m.project_id === projectId).map(m => manualSourceId(m.id)),
  ])
  return sources.filter(s => s.type === 'raw' ? stations.has(s.station) : own.has(s.id))
}
